import React, { useState, useEffect } from 'react';
import { BookOpen, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { getToken } from '../../services/authService';
import { playgroundCourses } from '../../data/playgroundData';

interface CourseProgress {
  course_id: string;
  total_interacted: number;
  total_completed: number;
  last_activity: string;
}

interface StudentProgress {
  user_id: number;
  name: string;
  email: string;
  access_level: number;
  courses: CourseProgress[];
}

interface DropoffLesson { index: number; completed_count: number; }
interface DropoffCohort { code: string; name: string; total_enrolled: number; lessons: DropoffLesson[]; }
interface DropoffData { cohorts: DropoffCohort[]; all: { total_enrolled: number; lessons: DropoffLesson[] }; }

const COURSE_TOTALS: Record<string, { label: string; total: number }> = Object.fromEntries(
  playgroundCourses.map(c => [c.courseId, { label: c.courseTitle.replace(/:.*/,'').trim(), total: c.lessons.length }])
);

const API_BASE = process.env.REACT_APP_AUTH_API
  || (process.env.NODE_ENV === 'production' ? '/api/admin' : 'http://localhost:8080/api/admin');

function adminUrl(path: string) {
  const base = API_BASE.replace(/\/auth$/, '/admin').replace(/\/admin$/, '/admin');
  return base.endsWith('/admin') ? `${base}/${path}` : `${API_BASE}/${path}`;
}

function fmtDate(d: string) {
  const date = new Date(d);
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
}

function daysSince(d: string) {
  return Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
}

export default function AdminProgress() {
  const [students, setStudents] = useState<StudentProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'individual' | 'agregado' | 'dropoff'>('individual');
  const [dropoffData, setDropoffData] = useState<DropoffData | null>(null);

  useEffect(() => {
    async function load() {
      const token = getToken();
      if (!token) return;
      try {
        const [resProgress, resDropoff] = await Promise.all([
          fetch(adminUrl('progress.php'), { headers: { Authorization: `Bearer ${token}` } }),
          fetch(adminUrl('bootcamp-dropoff.php'), { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (resProgress.ok) {
          const data = await resProgress.json();
          setStudents(data.students ?? []);
        }
        if (resDropoff.ok) {
          const data = await resDropoff.json();
          setDropoffData(data);
        }
      } catch { /* silent */ }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const allCourseIds = Array.from(new Set(students.flatMap(s => s.courses.map(c => c.course_id)))).sort();

  return (
    <div className="p-6 md:p-8">
      <div className="max-w-7xl">
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-2xl font-bold text-gray-800">Progreso de alumnos</h1>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setView('individual')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                view === 'individual' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Individual
            </button>
            <button
              onClick={() => setView('agregado')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                view === 'agregado' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Agregado
            </button>
            <button
              onClick={() => setView('dropoff')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                view === 'dropoff' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Drop-off
            </button>
          </div>
        </div>
        <p className="text-gray-500 text-sm mb-6">Lecciones completadas por alumno en cada curso/bootcamp.</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={<BookOpen size={20} />}
            value={String(students.length)}
            label="Alumnos activos"
            color="bg-purple-100 text-purple-600"
          />
          <StatCard
            icon={<CheckCircle size={20} />}
            value={String(students.reduce((s, st) => s + st.courses.reduce((c, cr) => c + cr.total_completed, 0), 0))}
            label="Lecciones completadas"
            color="bg-green-100 text-green-600"
          />
          <StatCard
            icon={<Clock size={20} />}
            value={inactiveCount(students)}
            label="Inactivos (+7 días)"
            color="bg-yellow-100 text-yellow-600"
          />
          <StatCard
            icon={<AlertCircle size={20} />}
            value={stuckCount(students)}
            label="Nunca completaron"
            color="bg-red-100 text-red-600"
          />
        </div>

        {view === 'dropoff' ? (
          <BootcampDropoffView data={dropoffData} />
        ) : view === 'individual' ? (
          <div className="bg-white rounded-xl border shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-3 font-semibold text-gray-600 sticky left-0 bg-gray-50 z-10">Alumno</th>
                  {allCourseIds.map(id => (
                    <th key={id} className="text-center p-3 font-semibold text-gray-600 whitespace-nowrap">
                      {COURSE_TOTALS[id]?.label || id}
                    </th>
                  ))}
                  <th className="text-center p-3 font-semibold text-gray-600">Última actividad</th>
                </tr>
              </thead>
              <tbody>
                {students.map(st => {
                  const lastAct = st.courses.reduce((latest, c) =>
                    c.last_activity > latest ? c.last_activity : latest, ''
                  );
                  const days = lastAct ? daysSince(lastAct) : 999;

                  return (
                    <tr key={st.user_id} className="border-b hover:bg-gray-50">
                      <td className="p-3 sticky left-0 bg-white z-10">
                        <div className="font-medium text-gray-800">{st.name}</div>
                        <div className="text-xs text-gray-400">{st.email}</div>
                      </td>
                      {allCourseIds.map(id => {
                        const cp = st.courses.find(c => c.course_id === id);
                        const total = COURSE_TOTALS[id]?.total || 1;
                        if (!cp) return <td key={id} className="text-center p-3 text-gray-300">—</td>;
                        const pct = Math.round((cp.total_completed / total) * 100);
                        return (
                          <td key={id} className="text-center p-3">
                            <ProgressPill completed={cp.total_completed} total={total} pct={pct} />
                          </td>
                        );
                      })}
                      <td className="text-center p-3">
                        <span className={`text-xs font-medium ${
                          days <= 3 ? 'text-green-600' : days <= 7 ? 'text-yellow-600' : 'text-red-500'
                        }`}>
                          {lastAct ? `${fmtDate(lastAct)} (${days}d)` : '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : view === 'agregado' ? (
          <ProgressAggregatedView students={students} allCourseIds={allCourseIds} />
        ) : null}
      </div>
    </div>
  );
}

const WEEK_LABELS = ['S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8', 'S9', 'S10'];

function BootcampDropoffView({ data }: { data: DropoffData | null }) {
  const [selectedCohort, setSelectedCohort] = useState<string>('all');

  if (!data) return <p className="text-gray-500 text-sm">Cargando datos de drop-off...</p>;

  const source = selectedCohort === 'all' ? data.all : data.cohorts.find(c => c.code === selectedCohort);
  if (!source) return null;

  const totalEnrolled = source.total_enrolled;
  const lessonsMap = new Map(source.lessons.map(l => [l.index, l.completed_count]));
  const maxWeeks = WEEK_LABELS.length;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Drop-off por semana — Bootcamp DE</h3>
            <p className="text-xs text-gray-400 mt-0.5">Cuántos alumnos completaron cada semana. Donde cae = donde se pierden.</p>
          </div>
          <select
            value={selectedCohort}
            onChange={e => setSelectedCohort(e.target.value)}
            className="text-xs border rounded-lg px-3 py-1.5 bg-white text-gray-700"
          >
            <option value="all">Todas las camadas</option>
            {data.cohorts.map(c => (
              <option key={c.code} value={c.code}>{c.name} ({c.total_enrolled} alumnos)</option>
            ))}
          </select>
        </div>
        <div className="p-6">
          <div className="flex items-end gap-2 h-64">
            {Array.from({ length: maxWeeks }, (_, i) => {
              const count = lessonsMap.get(i) ?? 0;
              const pct = totalEnrolled > 0 ? (count / totalEnrolled) * 100 : 0;
              const prev = i > 0 ? (lessonsMap.get(i - 1) ?? 0) : totalEnrolled;
              const drop = prev > 0 ? ((prev - count) / prev * 100) : 0;
              const isHeavyDrop = drop > 30;

              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="text-xs font-medium text-gray-700">{count}</div>
                  <div className="w-full relative" style={{ height: '200px' }}>
                    <div
                      className={`absolute bottom-0 w-full rounded-t-md transition-all ${
                        isHeavyDrop ? 'bg-red-400' : pct > 50 ? 'bg-green-400' : pct > 25 ? 'bg-yellow-400' : 'bg-orange-400'
                      }`}
                      style={{ height: `${Math.max(pct, 2)}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 font-medium">{WEEK_LABELS[i]}</div>
                  <div className={`text-[10px] ${isHeavyDrop ? 'text-red-600 font-bold' : 'text-gray-400'}`}>
                    {i > 0 ? `−${drop.toFixed(0)}%` : `${pct.toFixed(0)}%`}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
            <span>Total inscriptos: <strong className="text-gray-700">{totalEnrolled}</strong></span>
            <span>Completaron S1: <strong className="text-gray-700">{lessonsMap.get(0) ?? 0}</strong> ({totalEnrolled > 0 ? ((lessonsMap.get(0) ?? 0) / totalEnrolled * 100).toFixed(0) : 0}%)</span>
            <span>Llegaron a S{maxWeeks}: <strong className="text-gray-700">{lessonsMap.get(maxWeeks - 1) ?? 0}</strong> ({totalEnrolled > 0 ? ((lessonsMap.get(maxWeeks - 1) ?? 0) / totalEnrolled * 100).toFixed(0) : 0}%)</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">Detalle numérico</h3>
        </div>
        <div className="p-4">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-2 font-semibold text-gray-600">Semana</th>
                <th className="text-right p-2 font-semibold text-gray-600">Completaron</th>
                <th className="text-right p-2 font-semibold text-gray-600">% del total</th>
                <th className="text-right p-2 font-semibold text-gray-600">Caída vs anterior</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: maxWeeks }, (_, i) => {
                const count = lessonsMap.get(i) ?? 0;
                const pct = totalEnrolled > 0 ? (count / totalEnrolled * 100) : 0;
                const prev = i > 0 ? (lessonsMap.get(i - 1) ?? 0) : totalEnrolled;
                const drop = prev > 0 ? ((prev - count) / prev * 100) : 0;
                const isHeavyDrop = i > 0 && drop > 30;
                return (
                  <tr key={i} className={`border-b ${isHeavyDrop ? 'bg-red-50' : ''}`}>
                    <td className="p-2 font-medium text-gray-700">{WEEK_LABELS[i]}</td>
                    <td className="p-2 text-right text-gray-700">{count}</td>
                    <td className="p-2 text-right text-gray-600">{pct.toFixed(0)}%</td>
                    <td className={`p-2 text-right ${isHeavyDrop ? 'text-red-600 font-bold' : 'text-gray-400'}`}>
                      {i === 0 ? '—' : `−${drop.toFixed(0)}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ProgressAggregatedView({ students, allCourseIds }: { students: StudentProgress[]; allCourseIds: string[] }) {
  return (
    <div className="space-y-6">
      {/* Promedio de completitud por curso */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">Completitud promedio por curso</h3>
          <p className="text-xs text-gray-400 mt-0.5">Porcentaje promedio de lecciones completadas por los alumnos que tienen acceso a cada curso.</p>
        </div>
        <div className="p-4">
          <div className="space-y-3">
            {allCourseIds.map(courseId => {
              const courseInfo = COURSE_TOTALS[courseId] || { label: courseId, total: 1 };
              const studentsWithCourse = students.filter(s => s.courses.some(c => c.course_id === courseId));
              if (studentsWithCourse.length === 0) return null;

              const avgPct = studentsWithCourse.reduce((sum, st) => {
                const cp = st.courses.find(c => c.course_id === courseId);
                return sum + (cp ? (cp.total_completed / courseInfo.total) * 100 : 0);
              }, 0) / studentsWithCourse.length;

              const completedAll = studentsWithCourse.filter(st => {
                const cp = st.courses.find(c => c.course_id === courseId);
                return cp && cp.total_completed >= courseInfo.total;
              }).length;

              return (
                <div key={courseId} className="flex items-center gap-4">
                  <div className="w-36 text-sm font-medium text-gray-700 truncate">{courseInfo.label}</div>
                  <div className="flex-1 bg-gray-200 rounded-full h-4 relative overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        avgPct >= 80 ? 'bg-green-500' : avgPct >= 40 ? 'bg-blue-500' : 'bg-yellow-500'
                      }`}
                      style={{ width: `${Math.min(avgPct, 100)}%` }}
                    />
                  </div>
                  <div className="w-24 text-right text-xs text-gray-600">
                    {avgPct.toFixed(0)}% prom.
                  </div>
                  <div className="w-28 text-right text-xs text-gray-400">
                    {completedAll}/{studentsWithCourse.length} completaron
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Distribución de actividad */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">Distribución de actividad</h3>
          <p className="text-xs text-gray-400 mt-0.5">Cuántos alumnos están activos, inactivos, o nunca completaron una lección.</p>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(() => {
              const active = students.filter(st => {
                const lastAct = st.courses.reduce((l, c) => c.last_activity > l ? c.last_activity : l, '');
                return lastAct && daysSince(lastAct) <= 7;
              }).length;
              const inactive = students.filter(st => {
                const lastAct = st.courses.reduce((l, c) => c.last_activity > l ? c.last_activity : l, '');
                return lastAct && daysSince(lastAct) > 7;
              }).length;
              const never = students.filter(st => st.courses.every(c => c.total_completed === 0)).length;
              const total = students.length || 1;
              return (
                <>
                  <div className="text-center p-4 bg-green-50 rounded-xl">
                    <div className="text-2xl font-bold text-green-700">{active}</div>
                    <div className="text-xs text-green-600">{(active / total * 100).toFixed(0)}% activos (últimos 7 días)</div>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 rounded-xl">
                    <div className="text-2xl font-bold text-yellow-700">{inactive}</div>
                    <div className="text-xs text-yellow-600">{(inactive / total * 100).toFixed(0)}% inactivos (+7 días)</div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-xl">
                    <div className="text-2xl font-bold text-red-700">{never}</div>
                    <div className="text-xs text-red-600">{(never / total * 100).toFixed(0)}% sin completar nada</div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Tabla resumen por curso */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">Resumen por curso</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 font-semibold text-gray-600">Curso</th>
                <th className="text-right p-3 font-semibold text-gray-600">Alumnos</th>
                <th className="text-right p-3 font-semibold text-gray-600">Prom. completado</th>
                <th className="text-right p-3 font-semibold text-gray-600">Terminaron 100%</th>
                <th className="text-right p-3 font-semibold text-gray-600">Nunca empezaron</th>
              </tr>
            </thead>
            <tbody>
              {allCourseIds.map(courseId => {
                const courseInfo = COURSE_TOTALS[courseId] || { label: courseId, total: 1 };
                const withCourse = students.filter(s => s.courses.some(c => c.course_id === courseId));
                if (withCourse.length === 0) return null;

                const avgCompleted = withCourse.reduce((s, st) => {
                  const cp = st.courses.find(c => c.course_id === courseId);
                  return s + (cp?.total_completed || 0);
                }, 0) / withCourse.length;

                const finished = withCourse.filter(st => {
                  const cp = st.courses.find(c => c.course_id === courseId);
                  return cp && cp.total_completed >= courseInfo.total;
                }).length;

                const notStarted = withCourse.filter(st => {
                  const cp = st.courses.find(c => c.course_id === courseId);
                  return !cp || cp.total_completed === 0;
                }).length;

                return (
                  <tr key={courseId} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium text-gray-700">{courseInfo.label}</td>
                    <td className="text-right p-3">{withCourse.length}</td>
                    <td className="text-right p-3">{avgCompleted.toFixed(1)} / {courseInfo.total}</td>
                    <td className="text-right p-3">
                      <span className="text-green-600 font-medium">{finished}</span>
                      <span className="text-gray-400 text-xs ml-1">({(finished / withCourse.length * 100).toFixed(0)}%)</span>
                    </td>
                    <td className="text-right p-3">
                      <span className={notStarted > 0 ? 'text-red-500 font-medium' : 'text-gray-400'}>{notStarted}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ProgressPill({ completed, total, pct }: { completed: number; total: number; pct: number }) {
  const bgColor = pct === 100 ? 'bg-green-100 text-green-700' :
                  pct >= 50 ? 'bg-blue-100 text-blue-700' :
                  pct > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500';
  return (
    <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${bgColor}`}>
      {completed}/{total} ({pct}%)
    </span>
  );
}

function StatCard({ icon, value, label, color }: { icon: React.ReactNode; value: string; label: string; color: string }) {
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${color}`}>{icon}</div>
      <div className="text-xl font-bold text-gray-800">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function inactiveCount(students: StudentProgress[]): string {
  const count = students.filter(st => {
    const lastAct = st.courses.reduce((latest, c) => c.last_activity > latest ? c.last_activity : latest, '');
    return lastAct && daysSince(lastAct) > 7;
  }).length;
  return String(count);
}

function stuckCount(students: StudentProgress[]): string {
  const count = students.filter(st =>
    st.courses.every(c => c.total_completed === 0)
  ).length;
  return String(count);
}
